/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Typography } from '@mui/material';
import { memo, useCallback, useDeferredValue, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { BluAutoComplete } from '@bludata/components';
import { useUsersFilter } from 'packages/blu-booking/src/data_fetching/User/UserFilter';
import { RootState } from 'packages/blu-booking/src/store';

export const User = memo(() => {
  const { getValues, setValue, reset, trigger } = useFormContext();

  const service = useWatch({ name: 'serviceId' });
  const pointOfSale = useWatch({ name: 'pointOfSaleId' });
  const user = useWatch({ name: 'userId' });
  const shopSignId = useWatch({ name: 'shopSignId' });

  const { data: result, isLoading } = useUsersFilter({
    lockService: true,
    lockPointOfSale: true,
    serviceId: service,
    pointOfSaleId: pointOfSale,
    shopSignId,
  });

  const data = useDeferredValue(result);

  useEffect(() => {
    if (data === undefined) return;
    if (data.length === 0) return;

    const find = data.find((posl) => posl.id === user);
    if (find === undefined || pointOfSale === '') {
      setValue('userId', '');
    }
  }, [data, getValues, pointOfSale, reset, setValue, user]);

  const getData = useCallback(() => {
    return data ?? [];
  }, [data]);

  const handleOnChange = useCallback(
    (_: any, value: any) => {
      if (value === null) {
        setValue('userId', '');
        trigger();

        return;
      }
      setValue('userId', value.id);
      trigger();
    },
    [setValue, trigger]
  );

  return (
    <UserInner
      pointOfSaleId={pointOfSale}
      watch={user}
      data={getData()}
      isLoading={isLoading}
      handleOnChange={handleOnChange}
    />
  );
});

interface UserInnerProps {
  watch: any;
  isLoading: boolean;
  data: any;
  pointOfSaleId: string;
  handleOnChange: (event: any, value: any) => void;
}

const UserInner = memo(
  ({
    pointOfSaleId,
    watch,
    isLoading,
    data,
    handleOnChange,
  }: UserInnerProps) => {
    const { users } = useSelector(
      (state: RootState) => state.bluBookingCalendarFilters
    );

    const getValue = useCallback(
      () => (users !== undefined && watch in users ? users[watch] : null),
      [users, watch]
    );
    return (
      <div style={{ width: '49%' }}>
        <Typography
          variant="subtitle2"
          sx={{
            color:
              pointOfSaleId === undefined || pointOfSaleId === ''
                ? 'var(--disabled-color)'
                : undefined,
          }}
        >
          Utente
        </Typography>
        <BluAutoComplete
          value={getValue()}
          data={data}
          width="49%"
          textField={{
            placeholder: 'Seleziona un utente',
          }}
          disabled={pointOfSaleId === undefined || pointOfSaleId === ''}
          onChange={(event: any, value: any) => handleOnChange(event, value)}
          loading={isLoading}
          renderOption={(props: any, option: any) => (
            <Box component="li" {...props}>
              {option.username}
            </Box>
          )}
          isOptionEqualToValue={(option: any, value: any) =>
            option.id === value.id
          }
          getOptionLabel={(option: any) => `${option.username}`}
        />
      </div>
    );
  }
);
